"use client";
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@workspace/ui/components/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@workspace/ui/components/drawer";
import { Input } from "@workspace/ui/components/input";
import Image from "next/image";
import { CTAButton } from "../cta-button";
import { useModal } from "../providers/modal-provider";
import { saleImages, modalIds, discountImages } from "./constants";
import { getCookie, setCookie } from "@/lib/utils/common/common";
import Link from "next/link";
import { EventNames, sendEvent } from "@/lib/utils/analytics";
import { usePathname } from "next/navigation";
import { useMediaQuery } from "@/hooks/use-media-query";
import { XIcon } from "lucide-react";
import {
  NewLaunchTag,
  SaleNewLaunchTagMobile,
  SaleNewLaunchTagDesktop,
} from "@/components/new-launch-tag";
import { FALLBACK_DISCOUNT_CODE_PRICE } from "@/lib/utils/common/constants";
import { isSaleEnabled } from "@/lib/config/sale";
import { COOKIE_BRIDGE_EVENTS } from "@/lib/cookie-bridge";

const isSale = isSaleEnabled("discountModal");

function NewLaunchBadge({ variant = "mobile" }: { variant?: "mobile" | "desktop" }) {
  return (
    <div className="pointer-events-none absolute left-5 top-5 z-10">
      {isSale ? (
        variant === "desktop" ? <SaleNewLaunchTagDesktop /> : <SaleNewLaunchTagMobile />
      ) : (
        <NewLaunchTag size="compact" />
      )}
    </div>
  );
}

export function DiscountModal() {
  const [email, setEmail] = useState("");
  const [valid, setValid] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");
  const { modalId, handleCloseModal, handleOpenModal, modalContent } =
    useModal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pathname = usePathname();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const mobileImageSrc = isSale ? saleImages["ngMobile"] : discountImages["ng"];
  const desktopImageSrc = isSale ? saleImages["ngDesktop"] : discountImages["ng"];

  const handleClose = () => {
    sendEvent(EventNames.ANDROID_DISCOUNT_POPUP_CLOSE_BUTTON_CLICKED);
    setCookie("showed_discount_popup", "valid", 7);
    handleCloseModal();
  };

  const isOpen = modalId === modalIds.ngDiscount;
  const emailRegex =
    /^(?!.*\s)[A-Za-z0-9]+(?:\.[A-Za-z0-9]+)*@[A-Za-z0-9]+(?:\.[A-Za-z0-9]+)+$/;

  const handleCouponCode = async () => {
    if (!emailRegex.test(email)) {
      setValid(false);
      return;
    }

    setIsSubmitting(true);

    const sourceType = modalContent?.type ?? "unknown";
    sendEvent(EventNames.DISCOUNT_POPUP_EMAIL_SUBMITTED, { type: sourceType });

    const response = await fetch("/api/rebrand/send-discount", {
      method: "POST",
      body: JSON.stringify({
        email: email,
      }),
    });

    const data = await response.json();

    setIsSubmitting(false);

    if (!response.ok) {
      setValid(false);
      setError(data.message);
      return;
    }
    setCookie("showed_discount_popup", "valid", 7);
    setShowSuccess(true);
  };

  const [cookieModalOpen, setCookieModalOpen] = useState(false);

  useEffect(() => {
    const onModalShown = () => setCookieModalOpen(true);
    const onModalHidden = () => setCookieModalOpen(false);

    window.addEventListener(COOKIE_BRIDGE_EVENTS.MODAL_SHOWN, onModalShown);
    window.addEventListener(COOKIE_BRIDGE_EVENTS.MODAL_HIDDEN, onModalHidden);

    return () => {
      window.removeEventListener(COOKIE_BRIDGE_EVENTS.MODAL_SHOWN, onModalShown);
      window.removeEventListener(COOKIE_BRIDGE_EVENTS.MODAL_HIDDEN, onModalHidden);
    };
  }, []);

  useEffect(() => {
    if (cookieModalOpen) return;

    const cookie = getCookie("showed_discount_popup");
    const timerId = setTimeout(() => {
      if (modalId === "" && cookie !== "valid" && pathname === "/") {
        handleOpenModal(modalIds.ngDiscount, { type: "banner" });
        sendEvent(EventNames.DISCOUNT_POPUP_VIEWED);
      }
    }, 7000);

    return () => clearTimeout(timerId);
  }, [modalId, pathname, cookieModalOpen]);

  return (
    <>
      {isMobile && (
        <Drawer
          open={isOpen}
          onOpenChange={handleClose}
          repositionInputs={false}
        >
          <DrawerContent
            className="!border-0 !rounded-t-2xl max-h-[100vh] mt-0 md:hidden overflow-clip z-[200]"
            data-testid="discount-modal-content"
            id="discount-modal-content"
            overlayClassName="backdrop-blur-[20px]"
            overlayStyle={{ background: "rgba(0, 0, 0, 0.80)" }}
          >
            {!showSuccess && (
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 z-10 text-primary transition-colors"
                style={isSale ? { color: "white" } : undefined}
                id="dialog-popup-close"
                data-testid="dialog-popup-close"
                aria-label="Close"
              >
                <XIcon />
              </button>
            )}

            {!showSuccess ? (
              <>
                <div className={`relative w-full ${isSale ? "aspect-[828/524]" : "h-[190px] xs:h-[260px] md:h-[260px]"}`}>
                  <Image
                    src={mobileImageSrc}
                    alt="inito-kit"
                    className={`h-full w-full object-cover ${isSale ? "object-center" : "object-[50%_20%]"}`}
                    fill
                    sizes="100vw"
                    data-testid="discount-modal-image"
                  />
                  <NewLaunchBadge variant="mobile" />
                </div>
                <div className="px-5 py-8">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4 md:gap-5">
                      <NGSheetDiscountHeader />
                      <div className="flex flex-col gap-3">
                        <Input
                          placeholder="Your Email*"
                          type="email"
                          className={`border-0 border-b border-input rounded-none px-0 focus-visible:border-b-ring focus-visible:ring-0 aria-invalid:border-b-destructive aria-invalid:ring-0 ${
                            isSale
                              ? "placeholder:font-inter placeholder:font-medium placeholder:text-base placeholder:text-[#232221]"
                              : "placeholder:text-black"
                          }`}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setValid(emailRegex.test(e.target.value));
                          }}
                          data-testid="discount-modal-input"
                          id="discount-modal-input"
                        />
                        {!valid && (
                          <p
                            className="text-[10px] leading-[14px] xs:body-14 md:body-16 text-[#ec4a4a]"
                            data-testid="discount-modal-error"
                            id="discount-modal-error"
                          >
                            {error ||
                              "Invalid email address. Please enter a valid one."}
                          </p>
                        )}
                      </div>
                      <DrawerFooter className="w-full p-0">
                        <CTAButton
                          text="Get my coupon code"
                          textClassName="w-full !title-12 xs:!title-16 md:!title-18"
                          testId="discount-modal-cta"
                          handleCTAClick={handleCouponCode}
                          className="w-full rounded-[12px] py-[13px] md:w-full md:rounded-[14px] md:py-[15px]"
                          containerClassName="w-full md:max-w-full"
                          disabled={isSubmitting}
                        />
                      </DrawerFooter>
                    </div>
                    <div>
                      <p className="body-12 text-grey">
                        By submitting your email, you agree to the&nbsp;
                        <span className="text-secondary">
                          <Link href="/terms" className="underline">
                            Terms and Conditions
                          </Link>
                        </span>
                        &nbsp; and acknowledge the &nbsp;
                        <span className="text-secondary">
                          <Link href="/privacy-policy" className="underline">
                            Privacy Policy.
                          </Link>
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="px-5 pb-5 pt-8 gap-8 flex flex-col">
                <div className="flex flex-col gap-4 text-center">
                  <DrawerHeader
                    className="flex flex-col gap-4"
                    data-testid="discount-modal-success-header"
                  >
                    <div className="flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="64"
                        height="65"
                        viewBox="0 0 64 65"
                        fill="none"
                      >
                        <circle
                          cx="32"
                          cy="32.6152"
                          r="30.72"
                          stroke="#232221"
                          strokeWidth="2.56"
                        />
                        <path
                          d="M16 31.3587C20.7257 35.848 23.3752 38.365 28.1008 42.8544L48 21.7344"
                          stroke="#232221"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="flex flex-col gap-2 items-center">
                      <DrawerTitle
                        className="!title-20 xs:!title-32 text-center max-w-[340px]"
                        data-testid="discount-modal-success-title"
                        id="discount-modal-success-title"
                      >
                        Your discount code is on its way!
                      </DrawerTitle>
                      <DrawerDescription
                        className="!body-12 xs:!body-16 text-center text-secondary"
                        data-testid="discount-modal-success-description"
                        id="discount-modal-success-description"
                      >
                        Check your inbox shortly. If you don&apos;t see it in
                        your main inbox, don&apos;t forget to look in your Spam
                        or Promotions folder.
                      </DrawerDescription>
                    </div>
                  </DrawerHeader>
                </div>
                <DrawerFooter
                  data-testid="discount-modal-success-footer"
                  className="p-0"
                >
                  <div className="w-full">
                    <CTAButton
                      text="Got it"
                      textClassName="w-full !title-12 xs:!title-16 md:!title-18"
                      testId="discount-modal-success-cta"
                      handleCTAClick={() => {
                        handleCloseModal();
                        setShowSuccess(false);
                      }}
                      className="w-full rounded-[12px] py-[13px] md:w-full md:rounded-[14px] md:py-[15px]"
                      containerClassName="w-full md:max-w-full"
                      disabled={isSubmitting}
                    />
                  </div>
                </DrawerFooter>
              </div>
            )}
          </DrawerContent>
        </Drawer>
      )}

      {!isMobile && (
        <Dialog open={isOpen} onOpenChange={handleClose}>
          <DialogContent
            className={`border-0 mx-auto p-0 gap-0 hidden md:block overflow-clip rounded-2xl z-[200] ${isSale ? "sm:max-w-[562px]" : ""}`}
            data-testid="discount-modal-content"
            id="discount-modal-content"
            showCloseButton={!showSuccess}
            closeButtonStyle={isSale ? { color: "white" } : undefined}
            overlayClassName={isSale ? "backdrop-blur-[40px]" : ""}
            overlayStyle={isSale ? { background: "#000000CC" } : undefined}
          >
            {!showSuccess ? (
              <>
                <div className={`relative w-full ${isSale ? "h-[260px]" : "h-[190px] xs:h-[260px] md:h-[260px]"}`}>
                  <Image
                    src={desktopImageSrc}
                    alt="inito-kit"
                    className={`h-full w-full object-cover ${isSale ? "object-center" : "object-[50%_45%]"}`}
                    fill
                    sizes="562px"
                    data-testid="discount-modal-image"
                  />
                  <NewLaunchBadge variant="desktop" />
                </div>
                <div className={isSale ? "p-8" : "p-5 md:p-8"}>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4 md:gap-5">
                      <NGDiscountHeader />
                      <div className="flex flex-col gap-3">
                        <Input
                          placeholder="Your Email*"
                          type="email"
                          className={`border-0 border-b border-input rounded-none px-0 focus-visible:border-b-ring focus-visible:ring-0 aria-invalid:border-b-destructive aria-invalid:ring-0 ${
                            isSale
                              ? "placeholder:font-inter placeholder:font-medium placeholder:text-base placeholder:text-[#232221]"
                              : "placeholder:text-black"
                          }`}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setValid(emailRegex.test(e.target.value));
                          }}
                          data-testid="discount-modal-input"
                          id="discount-modal-input"
                        />
                        {!valid && (
                          <p
                            className="body-14 md:body-16 text-[#ec4a4a]"
                            data-testid="discount-modal-error"
                            id="discount-modal-error"
                          >
                            {error ||
                              "Invalid email address. Please enter a valid one."}
                          </p>
                        )}
                      </div>
                      <DialogFooter className="w-full">
                        <CTAButton
                          text="Get my coupon code"
                          textClassName="w-full !title-16 md:!title-18"
                          testId="discount-modal-cta"
                          handleCTAClick={handleCouponCode}
                          className="w-full rounded-[12px] py-[13px] md:w-full md:rounded-[14px] md:py-[15px]"
                          containerClassName="w-full md:max-w-full"
                          disabled={isSubmitting}
                        />
                      </DialogFooter>
                    </div>
                    <div>
                      <p className="body-12 text-grey">
                        By submitting your email, you agree to the&nbsp;
                        <span className="text-secondary">
                          <Link href="/terms" className="underline">
                            Terms and Conditions
                          </Link>
                        </span>
                        &nbsp; and acknowledge the &nbsp;
                        <span className="text-secondary">
                          <Link href="/privacy-policy" className="underline">
                            Privacy Policy.
                          </Link>
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-8 gap-6 flex flex-col">
                <div className="flex flex-col gap-4 text-center">
                  <DialogHeader
                    className="flex flex-col gap-4"
                    data-testid="discount-modal-success-header"
                  >
                    <div className="flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="64"
                        height="65"
                        viewBox="0 0 64 65"
                        fill="none"
                      >
                        <circle
                          cx="32"
                          cy="32.6152"
                          r="30.72"
                          stroke="#232221"
                          strokeWidth="2.56"
                        />
                        <path
                          d="M16 31.3587C20.7257 35.848 23.3752 38.365 28.1008 42.8544L48 21.7344"
                          stroke="#232221"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="flex flex-col gap-2 items-center">
                      <DialogTitle
                        className="!title-20 xs:!title-32 text-center max-w-[340px]"
                        data-testid="discount-modal-success-title"
                        id="discount-modal-success-title"
                      >
                        Your discount code is on its way!
                      </DialogTitle>
                      <div className="px-[50px] w-full">
                        <DialogDescription
                          className="!body-16 text-center text-secondary"
                          data-testid="discount-modal-success-description"
                          id="discount-modal-success-description"
                        >
                          Check your inbox shortly. If you don&apos;t see it in
                          your main inbox, don&apos;t forget to look in your
                          Spam or Promotions folder.
                        </DialogDescription>
                      </div>
                    </div>
                  </DialogHeader>
                </div>
                <DialogFooter data-testid="discount-modal-success-footer">
                  <div className="w-full">
                    <CTAButton
                      text="Got it"
                      textClassName="w-full !title-16 md:!title-18"
                      testId="discount-modal-success-cta"
                      handleCTAClick={() => {
                        handleCloseModal();
                        setShowSuccess(false);
                      }}
                      className="w-full rounded-[12px] py-[13px] md:w-full md:rounded-[14px] md:py-[15px]"
                      containerClassName="w-full md:max-w-full"
                      disabled={isSubmitting}
                    />
                  </div>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export const DiscountTitle = ({
  children,
  ...props
}: React.PropsWithChildren) => {
  return (
    <DialogTitle
      className="title-20 xs:title-32 text-left"
      {...props}
      id="discount-modal-title"
      data-testid="discount-modal-title"
    >
      {children}
    </DialogTitle>
  );
};

const SaleTitle = () => {
  const discountAmount = FALLBACK_DISCOUNT_CODE_PRICE.ng;
  return <>{`Get $${discountAmount} off on your Inito Fertility Monitor`}</>;
};

const SaleDescription = () => {
  const discountAmount = FALLBACK_DISCOUNT_CODE_PRICE.ng;
  return (
    <div
      id="dialog-discount-description"
      data-testid="dialog-discount-description"
      className="flex flex-col gap-[10px] text-left"
    >
      <p className="title-12 xs:title-16 text-secondary">
        Taking control of your reproductive health is now easier than ever.
        Enter your email and we will send you a coupon code for $
        {discountAmount} off*
      </p>
      <p className="p-[6px] text-grey body-12 bg-light-grey rounded-[5px]">
        *Valid on first time purchase of the InSight Wireless Reader
      </p>
    </div>
  );
};

const NGDiscountHeader = () => {
  const discountAmount = FALLBACK_DISCOUNT_CODE_PRICE.ng;

  return (
    <DiscountHeader>
      <DiscountTitle>
        {isSale
          ? <SaleTitle />
          : `Get $${discountAmount} off on your Inito Fertility Monitor`}
      </DiscountTitle>
      <DiscountDescription>
        {isSale ? (
          <SaleDescription />
        ) : (
          <div className="flex flex-col gap-[10px]">
            <p className="title-16 text-secondary">
              Taking control of your reproductive health is now easier than
              ever. Enter your email and we will send you a coupon code for $
              {discountAmount} off*
            </p>
            <p className="p-[6px] text-grey body-12 bg-light-grey rounded-[5px]">
              *Valid on first time purchase of the InSight Wireless Reader
            </p>
          </div>
        )}
      </DiscountDescription>
    </DiscountHeader>
  );
};

const NGSheetDiscountHeader = () => {
  const discountAmount = FALLBACK_DISCOUNT_CODE_PRICE.ng;

  return (
    <DrawerHeader className="p-0">
      <DrawerTitle
        className="title-20 xs:title-32 text-left"
        data-testid="discount-modal-title"
        id="discount-modal-title"
      >
        {isSale ? <SaleTitle /> : `Get $${discountAmount} off on your Inito Fertility Monitor`}
      </DrawerTitle>
      <DrawerDescription
        id="dialog-discount-description"
        data-testid="dialog-discount-description"
        asChild
      >
        {isSale ? (
          <SaleDescription />
        ) : (
          <div className="flex flex-col gap-[10px] text-left">
            <p className="title-12 xs:title-16 text-secondary">
              Taking control of your reproductive health is now easier than
              ever. Enter your email and we will send you a coupon code for $
              {discountAmount} off*
            </p>
            <p className="p-[6px] text-grey body-12 bg-light-grey rounded-[5px]">
              *Valid on first time purchase of the InSight Wireless Reader
            </p>
          </div>
        )}
      </DrawerDescription>
    </DrawerHeader>
  );
};

export const DiscountDescription = ({ children }: React.PropsWithChildren) => {
  return (
    <DialogDescription
      className="text-left"
      id="dialog-discount-description"
      data-testid="dialog-discount-description"
      asChild
    >
      {children}
    </DialogDescription>
  );
};

export const DiscountHeader = ({ children }: React.PropsWithChildren) => {
  return (
    <DialogHeader
      id="discount-modal-header"
      data-testid="discount-modal-header"
    >
      <div className="flex flex-col gap-3 md:gap-4">{children}</div>
    </DialogHeader>
  );
};
